import { useState } from 'react';
import {
  useTranslate,
  useCustomMutation,
  useList,
  useNotification,
} from '@refinedev/core';
import { DateField } from '@refinedev/antd';
import {
  Typography,
  Tag,
  Divider,
  Table,
  Button,
  Alert,
  Space,
  Tooltip,
  Modal,
  Checkbox,
} from 'antd';
import type {
  ContentTranscodingJobOut,
  ContentTranscodingTriggerOut,
  TranscodingJobStatus,
} from '@/api/generated/schemas';

import { transcodingStatusColors } from '@/pages/content_catalog/products/constants';

const { Title } = Typography;

export const ProductTranscodingJobsSection = ({
  productId,
}: {
  productId: number;
}) => {
  const t = useTranslate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [force, setForce] = useState(false);
  const [triggerResult, setTriggerResult] =
    useState<ContentTranscodingTriggerOut | null>(null);
  const { mutateAsync } = useCustomMutation();
  const { open } = useNotification();

  const { result, query } = useList<ContentTranscodingJobOut>({
    resource: 'content_catalog.transcoding_jobs',
    filters: [{ field: 'product_id', operator: 'eq', value: productId }],
    sorters: [{ field: 'id', order: 'desc' }],
    pagination: { pageSize: 100 },
  });

  const handleTrigger = async () => {
    setSubmitting(true);
    try {
      const { data } = await mutateAsync({
        url: `content_catalog/products/${productId}/transcoding_jobs`,
        method: 'post',
        values: { force },
      });
      // Success: close confirm, show the trigger breakdown, and refetch.
      setConfirmOpen(false);
      setForce(false);
      setTriggerResult(data as ContentTranscodingTriggerOut);
      query.refetch();
    } catch (e) {
      // Keep the modal open with inputs intact on failure.
      open?.({
        type: 'error',
        message: t('content_catalog.transcoding_jobs.trigger.error'),
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Divider />
      <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Title level={4} style={{ margin: 0 }}>
          {t('content_catalog.transcoding_jobs.title')}
        </Title>
        <Button type="primary" onClick={() => setConfirmOpen(true)}>
          {t('content_catalog.transcoding_jobs.trigger.button')}
        </Button>
      </Space>
      <Modal
        open={confirmOpen}
        title={t('content_catalog.transcoding_jobs.trigger.modal_title')}
        onCancel={() => setConfirmOpen(false)}
        onOk={handleTrigger}
        okButtonProps={{ loading: submitting }}
        cancelButtonProps={{ disabled: submitting }}
        okText={t('content_catalog.transcoding_jobs.trigger.button')}
        cancelText={t('actions.cancel')}
        destroyOnClose
      >
        <Typography.Paragraph>
          {t('content_catalog.transcoding_jobs.trigger.confirm_message')}
        </Typography.Paragraph>
        <Checkbox checked={force} onChange={(e) => setForce(e.target.checked)}>
          {t('content_catalog.transcoding_jobs.trigger.force_label')}
        </Checkbox>
        <Typography.Paragraph
          type="secondary"
          style={{ fontSize: 12, marginTop: 4 }}
        >
          {t('content_catalog.transcoding_jobs.trigger.force_hint')}
        </Typography.Paragraph>
      </Modal>

      <Modal
        open={triggerResult !== null}
        title={t('content_catalog.transcoding_jobs.trigger.result_title')}
        onCancel={() => setTriggerResult(null)}
        onOk={() => setTriggerResult(null)}
        cancelButtonProps={{ style: { display: 'none' } }}
        okText={t('actions.close')}
        destroyOnClose
      >
        {triggerResult && (
          <>
            <Space size="large" style={{ marginBottom: 12 }}>
              <Typography.Text>
                {t('content_catalog.transcoding_jobs.trigger.triggered')}:{' '}
                <strong>{triggerResult.triggered}</strong>
              </Typography.Text>
              <Typography.Text>
                {t('content_catalog.transcoding_jobs.trigger.skipped')}:{' '}
                <strong>{triggerResult.skipped}</strong>
              </Typography.Text>
            </Space>
            <Table
              dataSource={triggerResult.files}
              rowKey="asset_file_id"
              pagination={false}
              size="small"
            >
              <Table.Column
                dataIndex="asset_file_id"
                title={t(
                  'content_catalog.transcoding_jobs.fields.asset_file_id'
                )}
              />
              <Table.Column
                dataIndex="status"
                title={t('content_catalog.transcoding_jobs.fields.status')}
              />
              <Table.Column
                dataIndex="reason"
                title={t('content_catalog.transcoding_jobs.trigger.reason')}
                render={(v: string | null) => v ?? '-'}
              />
            </Table>
          </>
        )}
      </Modal>

      {query.isError ? (
        <Alert type="error" message={query.error?.message ?? 'Error'} />
      ) : result.data.length === 0 && !query.isLoading ? (
        <Typography.Text type="secondary">
          {t('content_catalog.transcoding_jobs.empty')}
        </Typography.Text>
      ) : (
        <Table<ContentTranscodingJobOut>
          dataSource={result.data}
          rowKey="id"
          loading={query.isLoading}
          pagination={false}
          size="small"
          scroll={{ x: true }}
          expandable={{
            expandedRowRender: (record) => (
              <pre style={{ fontSize: 12, margin: 0, whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(record, null, 2)}
              </pre>
            ),
          }}
        >
          <Table.Column dataIndex="id" title={t('base.fields.id')} />
          <Table.Column
            dataIndex="status"
            title={t('content_catalog.transcoding_jobs.fields.status')}
            render={(value: TranscodingJobStatus) => (
              <Tag color={transcodingStatusColors[value] ?? 'default'}>
                {t(`content_catalog.transcoding_jobs.status.${value}`, {
                  defaultValue: value,
                })}
              </Tag>
            )}
          />
          <Table.Column
            dataIndex="lcps_content_id"
            title={t('content_catalog.transcoding_jobs.fields.lcps_content_id')}
            render={(value: string | null) =>
              value ? <Typography.Text code>{value}</Typography.Text> : '-'
            }
          />
          <Table.Column
            dataIndex="ebook_characters_count"
            title={t('content_catalog.transcoding_jobs.fields.characters')}
            render={(value: number | null) =>
              value != null ? value.toLocaleString() : '-'
            }
          />
          <Table.Column
            dataIndex="requested_at"
            title={t('content_catalog.transcoding_jobs.fields.requested_at')}
            render={(value: string | null) =>
              value ? <DateField value={value} format="LLL" /> : '-'
            }
          />
          <Table.Column
            dataIndex="completed_at"
            title={t('content_catalog.transcoding_jobs.fields.completed_at')}
            render={(value: string | null) =>
              value ? <DateField value={value} format="LLL" /> : '-'
            }
          />
          <Table.Column<ContentTranscodingJobOut>
            dataIndex="error_code"
            title={t('content_catalog.transcoding_jobs.fields.error')}
            render={(_, record) =>
              record.error_code ? (
                <Tooltip title={record.error_message ?? undefined}>
                  <Tag color={record.error_retryable ? 'warning' : 'error'}>
                    {record.error_code}
                  </Tag>
                </Tooltip>
              ) : (
                '-'
              )
            }
          />
          <Table.Column<ContentTranscodingJobOut>
            title={t('content_catalog.transcoding_jobs.fields.artifacts')}
            render={(_, record) => {
              const links = [
                {
                  url: record.master_artifact_url,
                  label: t('content_catalog.transcoding_jobs.artifacts.master'),
                },
                {
                  url: record.demo_artifact_url,
                  label: t('content_catalog.transcoding_jobs.artifacts.demo'),
                },
              ].filter((l) => l.url);

              return links.length > 0 ? (
                <Space size={4}>
                  {links.map((l) => (
                    <Typography.Link
                      key={l.label}
                      href={l.url!}
                      target="_blank"
                    >
                      {l.label}
                    </Typography.Link>
                  ))}
                </Space>
              ) : (
                '-'
              );
            }}
          />
        </Table>
      )}
    </>
  );
};
